import sortBy from 'lodash/sortBy';
import getConfig from 'next/config';

/**
 * @function
 * @param {LinkDefApiProps} [element] - The link element containing URL, text, target, and title.
 * @returns {NavLinkProps | undefined} The props for the navigation link or undefined if element is undefined.
 * @description This function takes a link element and returns an object with the href, text,
 *           target, and title. The href is the URL, the text is the link text or URL if text is not provided,
 *          the target is set to '_self' or '_blank' based on the element's target, and the title is the element's title.
 *
 * @example
 * const element = { url: '/home', text: 'Home', target: 'SELF', title: 'Go to Home' };
 * const navLinkProps = getNavLinkProps(element);
 * // navLinkProps will be { href: '/home', text: 'Home', target: '_self', title: 'Go to Home' }
 */
export function getNavLinkProps(element) {
  if (!element) {
    return undefined;
  }

  const { target, url, description, image, text } = element;

  return {
    url,
    text: text || url,
    target: target === 'SELF' ? '_self' : '_blank',
    description,
    image,
  };
}

/**
 * @function
 * @param {string} language - The language code to be formatted.
 * @returns {string} The formatted language code.
 * @description This function takes a language code and formats it by splitting it
 *             at the hyphen and returning the first part. This is useful for
 *            extracting the language from a full locale string (e.g., 'en-US' to 'en').
 *
 * @example
 * const language = 'en-US';
 * const formattedLanguage = getLanguageText(language);
 * // formattedLanguage will be 'en'.
 */
const getLanguageText = (language) => language.split('-')[0];

/**
 * @function
 * @param {TranslationDefApiProps} element - The translation element containing language and path.
 * @returns {LanguageLinkProps}
 * @description This function takes a translation element and returns an object with the
 *            language ID, text, and href. The ID is the language code, the text is the
 *           formatted language text, and the href is the URL constructed from the
 *          language and path.
 * @example
 * const element = { language: 'en-US', path: '/home' };
 * const languageLinkProps = getLanguageLinkProps(element);
 * // languageLinkProps will be { id: 'en-US', text: 'en', href: '/en-US/home' }
 */
const getLanguageLinkProps = (element) => ({
  id: element.language,
  text: getLanguageText(element.language),
  href: `/${element.language}${element.path}`,
});

/**
 * @function
 * @param {PageContentProps} pageContent - The translation element containing language and path.
 * @returns {Array<{ id: string; text: string; href: string }>}
 * @description This function takes a translation element and returns an object with the
 *            language ID, text, and href. The ID is the language code, the text is the
 *           formatted language text, and the href is the URL constructed from the
 *          language and path.
 * @example
 * const element = { language: 'en-US', path: '/home' };
 * const languageLinkProps = getLanguageLinkProps(element);
 * // languageLinkProps will be { id: 'en-US', text: 'en', href: '/en-US/home' }
 */
export const getLanguages = (pageContent) => {
  if (pageContent.translations) {
    return sortBy(
      [
        ...pageContent.translations.map((translation) =>
          getLanguageLinkProps(translation),
        ),
        {
          id: pageContent.language,
          text: getLanguageText(pageContent.language),
          href: '#',
        },
      ],
      ['id'],
    );
  }
  return [];
};

/**
 * @function
 * @param {SiteApiProps} siteContent - The site content containing edit mode menu.
 * @returns {EditorNavProps | undefined }
 * @description This function takes site content and returns an object containing
 *           the navigation links for the edit mode menu. Each link has text and href properties.
 *          The href is constructed using the cmsBaseUrl from the public runtime config.
 * @example
 * const siteContent = {
 *  editModeMenu: [
 *   { text: 'Home', url: '/home' },
 *  { text: 'About', url: '/about' },
 * ],
 * };
 * const editModeMenuProps = getEditModeMenu(siteContent);
 * // editModeMenuProps will be {
 * //   navLinks: [
 * //     { text: 'Home', href: 'https://cms.example.com/home' },
 * //     { text: 'About', href: 'https://cms.example.com/about' },
 * //   ],
 * // };
 */
export const getEditModeMenu = (siteContent) => {
  const { publicRuntimeConfig } = getConfig();
  if (!siteContent.editModeMenu) {
    return undefined;
  }

  return {
    navLinks: siteContent.editModeMenu.map((vals) => ({
      text: vals.text,
      href: publicRuntimeConfig.cmsBaseUrl + vals.url,
    })),
  };
};

/**
 * @function
 * @param {PageContentProps} pageContent - The page content containing edit mode link.
 * @returns {EditButtonProps | undefined }
 * @description This function takes page content and returns an object containing
 *          the text and href for the edit mode link. The href is constructed using
 *         the cmsBaseUrl from the public runtime config.
 * @example
 * const pageContent = {
 *  editModeLink: {
 *   text: 'Edit Page',
 *   url: '/edit-page',
 *  },
 * };
 * const editLinkProps = getPageEditLinkProps(pageContent);
 * // editLinkProps will be {
 * //   text: 'Edit Page',
 * //   href: 'https://cms.example.com/edit-page',
 * //   onHover: () => {},
 * // };
 */
export const getPageEditLinkProps = (pageContent) => {
  const { publicRuntimeConfig } = getConfig();
  if (!pageContent.editModeLink) {
    return undefined;
  }

  return {
    text: pageContent.editModeLink.text || 'Edit PAGE',
    href: publicRuntimeConfig.cmsBaseUrl + pageContent.editModeLink.url,
    onHover: () => {},
  };
};
